import apis from '#store/api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataApiResponse, DataCollectionApiResponse } from './types';

export const USERS_KEY = 'users';

interface UserSettings {
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    email?: string;
}

export interface User extends UserSettings {
    id?: string;
    role?: string;
}

export interface CurrentUser extends User {
    language?: string;
    unit?: string;
    tenantType: string;
}

interface GetUsersParams {
    skip: number;
    take: number;
}

export interface CreateUserDto extends UserSettings {
    role?: string;
    password?: string;
}

interface CreateUserParams {
    user: CreateUserDto;
}

interface UpdateUserParams {
    id: string;
    user: User;
}

interface UpdateUserSettingsParams {
    userSettings: UserSettings;
}

interface ResetPasswordParams {
    id: string;
    password: string;
}

interface BatchCreateUsersParams {
    users: {
        emails?: string[];
        password?: string;
        genericAddress?: string;
        numberOfUsers?: number;
        role?: string;
    }

}

interface BatchDeleteUsersParams {
    ids: string[];
}

interface BatchResetPasswordParams {
    ids: string[];
    password: string;
}

interface BatchUpdateRoleParams {
    ids: string[];
    role: string;
}

// Fetch users list
export const useUsers = (params: GetUsersParams) => {
    return useQuery({
        queryKey: [USERS_KEY, params],
        queryFn: async () => {
            const response = await apis.users.getUsersList({ params });
            return response.data as DataCollectionApiResponse<User>;
        },
        refetchOnMount: 'always',
        refetchOnWindowFocus: false
    });
};

// Create user
export const useCreateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: CreateUserParams) => {
            const response = await apis.users.createUser({ data: params });
            return response.data as DataApiResponse<User>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        },
    });
};

// Update user
export const useUpdateUser = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: UpdateUserParams) => {
            const response = await apis.users.updateUser({ data: params });
            return response.data as DataApiResponse<User>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        },
    });
};

// Reset password
export const useResetPassword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: ResetPasswordParams) => {
            const response = await apis.users.resetPassword({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        }
    });
};

export const useUpdateUserSettings = () => {
    const queryClient = useQueryClient();
  
    return useMutation({
      mutationFn: async (params: UpdateUserSettingsParams) => {
        const response = await apis.users.updateUserSettings({ data: params });
        return response.data as DataApiResponse<User>;
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
      },
    });
  };

// Batch

// Batch create users 
export const useBatchCreateUsers = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchCreateUsersParams) => {
            const response = await apis.users.batchCreateUsers({ data: params });
            return response.data as DataCollectionApiResponse<User>;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        },
    });
};

// Batch delete users
export const useBatchDeleteUsers = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchDeleteUsersParams) => {
            const response = await apis.users.batchDeleteUsers({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        }
    });
};

// Batch reset password
export const useBatchResetPassword = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchResetPasswordParams) => {
            const response = await apis.users.batchResetPassword({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        }
    });
};

// Batch update role
export const useBatchUpdateRole = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: BatchUpdateRoleParams) => {
            const response = await apis.users.batchUpdateRole({ data: params });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: [USERS_KEY] });
        }
    });
};
